import { useCallback, useEffect, useRef, useState } from 'react';

import { EntitlementViewBase } from '@amzn/coral_com-amazon-sssapinterface-model-structures-credits';

import { EAC_ENTITLEMENT_STATE, ORDER_ATTEMPT_STATUS } from 'src/enums';

export interface OrderError {
  isRetryable: boolean;
}

interface UseAsyncOrderPollingOptions {
  entitlementId: string | undefined;
  getEacEntitlement: (entitlementId: string) => Promise<{ entitlement: EntitlementViewBase }>;
  onSuccess: () => void;
  onError: (err: Error) => void;
  pollingIntervalMs?: number;
  pollingTimeoutMs?: number;
}

interface UseAsyncOrderPollingResult {
  isPolling: boolean;
  orderError: OrderError | null;
  startPolling: (workflowExecutionId: string) => void;
  clearError: () => void;
  handlePageLoadGuard: (entitlement: EntitlementViewBase | null | undefined) => void;
}

const DEFAULT_POLLING_INTERVAL_MS = 2500;
const DEFAULT_POLLING_TIMEOUT_MS = 90000;

export const useAsyncOrderPolling = ({
  entitlementId,
  getEacEntitlement,
  onSuccess,
  onError,
  pollingIntervalMs = DEFAULT_POLLING_INTERVAL_MS,
  pollingTimeoutMs = DEFAULT_POLLING_TIMEOUT_MS,
}: UseAsyncOrderPollingOptions): UseAsyncOrderPollingResult => {
  const [isPolling, setIsPolling] = useState(false);
  const [orderError, setOrderError] = useState<OrderError | null>(null);

  const pollingTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const pollingStartTimeRef = useRef<number>(0);
  const currentWorkflowIdRef = useRef<string | null>(null);
  const hasGuardFiredRef = useRef(false);

  const stopPolling = useCallback(() => {
    if (pollingTimeoutRef.current) {
      clearTimeout(pollingTimeoutRef.current);
      pollingTimeoutRef.current = null;
    }
    setIsPolling(false);
  }, []);

  const pollEntitlement = useCallback(async () => {
    const elapsed = Date.now() - pollingStartTimeRef.current;
    if (elapsed >= pollingTimeoutMs) {
      stopPolling();
      setOrderError({ isRetryable: false });
      return;
    }

    if (!entitlementId) return;

    try {
      const result = await getEacEntitlement(entitlementId);
      const { entitlementState, orderAttempt } = result.entitlement;

      if (entitlementState === EAC_ENTITLEMENT_STATE.PURCHASING) {
        stopPolling();
        onSuccess();
      } else if (orderAttempt?.status === ORDER_ATTEMPT_STATUS.FAILED) {
        // Only treat as genuine failure if workflowExecutionId matches the current attempt.
        // If it doesn't match (or is null), it's a stale failure from a previous attempt — keep polling.
        if (orderAttempt.workflowExecutionId === currentWorkflowIdRef.current) {
          stopPolling();
          setOrderError({ isRetryable: true });
        }
      }
    } catch (err) {
      // Transient polling read failure — don't stop polling.
      // The order workflow is still running on the backend.
      // If all polls fail, the timeout will show the non-retryable error.
      onError(err as Error);
    }
  }, [entitlementId, getEacEntitlement, stopPolling, onSuccess, onError, pollingTimeoutMs]);

  const pollEntitlementRef = useRef(pollEntitlement);
  useEffect(() => {
    pollEntitlementRef.current = pollEntitlement;
  }, [pollEntitlement]);

  const beginPolling = useCallback(() => {
    if (pollingTimeoutRef.current) {
      clearTimeout(pollingTimeoutRef.current);
    }
    setIsPolling(true);
    setOrderError(null);
    pollingStartTimeRef.current = Date.now();

    const poll = async () => {
      await pollEntitlementRef.current();
      if (pollingTimeoutRef.current) {
        pollingTimeoutRef.current = setTimeout(poll, pollingIntervalMs);
      }
    };
    pollingTimeoutRef.current = setTimeout(poll, pollingIntervalMs);
  }, [pollingIntervalMs]);

  const startPolling = useCallback(
    (workflowExecutionId: string) => {
      currentWorkflowIdRef.current = workflowExecutionId;
      beginPolling();
    },
    [beginPolling]
  );

  const handlePageLoadGuard = useCallback(
    (entitlement: EntitlementViewBase | null | undefined) => {
      if (!entitlement?.orderAttempt) return;
      if (hasGuardFiredRef.current) return;

      const { status, workflowExecutionId } = entitlement.orderAttempt;

      if (status === ORDER_ATTEMPT_STATUS.IN_PROGRESS) {
        hasGuardFiredRef.current = true;
        // On page-load resume, use the orderAttempt's workflowExecutionId if available
        currentWorkflowIdRef.current = workflowExecutionId ?? null;
        beginPolling();
      } else if (status === ORDER_ATTEMPT_STATUS.FAILED) {
        hasGuardFiredRef.current = true;
        setOrderError({ isRetryable: true });
      }
    },
    [beginPolling]
  );

  const clearError = useCallback(() => {
    setOrderError(null);
  }, []);

  // Cleanup polling on unmount
  useEffect(
    () => () => {
      if (pollingTimeoutRef.current) {
        clearTimeout(pollingTimeoutRef.current);
        pollingTimeoutRef.current = null;
      }
      hasGuardFiredRef.current = false;
    },
    []
  );

  return { isPolling, orderError, startPolling, clearError, handlePageLoadGuard };
};
